import { useEffect, useMemo, useState } from 'react'
import { useAppStore, attentionBadgeTotal } from '../store/app'
import { bridge } from '../bridge'
import type { ChatRoom } from '../store/types'
import { NavBar, NavBarButton } from '../components/NavBar'
import {
  startWS,
  fetchPendingApprovals,
  fetchAttention,
  listProvisioningProposals,
  normalizePendingApprovalEntry,
} from '../api/ws'
import {
  PUSH_BANNER_DISMISSED_KEY,
  shouldHideBrowserPushBanner,
} from './agents/browserPushBanner'
import type { Row } from './agents/AgentRow'
import { AgentRow, AgentActionSheet } from './agents/AgentRow'
import { RoomRow, RoomActionMenu } from './chat/ChatRoomRow'
import { OrgPickerDropdown } from '../components/OrgPickerDropdown'
import './HomeScreen.css'

type Section = 'chats' | 'full_time' | 'temp'

export function HomeScreen() {
  const agentInstances = useAppStore((s) => s.agentInstances)
  const positions = useAppStore((s) => s.organizationPositions)
  const hosts = useAppStore((s) => s.hosts)
  const connectionStatus = useAppStore((s) => s.connectionStatus)
  const chatRooms = useAppStore((s) => s.chatRooms)
  const chatUnreadCounts = useAppStore((s) => s.chatUnreadCounts)
  const chatNotifModes = useAppStore((s) => s.chatNotifModes)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const pushNav = useAppStore((s) => s.pushNav)
  const availableOrganizations = useAppStore((s) => s.availableOrganizations)
  const pendingTempSpawn = useAppStore((s) => s.pendingTempSpawn)
  const tempSpawnError = useAppStore((s) => s.tempSpawnError)
  const setTempSpawnError = useAppStore((s) => s.setTempSpawnError)
  const organizationID = useAppStore((s) => s.organizationID)

  const currentOrg = availableOrganizations.find((o) => o.is_current)
  const currentOrgName = currentOrg?.name ?? 'Hearth'
  // The bell mirrors the app-icon badge: everything waiting on you, across every
  // household. One number, everywhere (attentionBadgeTotal).
  const attentionCount = useAppStore((s) => attentionBadgeTotal(s, s.pendingRequests.length))
  // Rooms with unread replies in threads you follow, for the room rows' 🧵.
  const attentionThreads = useAppStore((s) => s.attentionThreads)
  const unreadThreadsByRoom = useMemo(() => {
    const out: Record<string, number> = {}
    for (const t of attentionThreads) out[t.room_id] = (out[t.room_id] ?? 0) + 1
    return out
  }, [attentionThreads])
  const connected = connectionStatus === 'connected'

  const [orgPickerOpen, setOrgPickerOpen] = useState(false)
  const canSwitchOrg = availableOrganizations.length > 1

  const [collapsed, setCollapsed] = useState<Record<Section, boolean>>({
    chats: false,
    full_time: false,
    temp: false,
  })
  const [actionMenuRow, setActionMenuRow] = useState<Row | null>(null)
  const [menuRoom, setMenuRoom] = useState<ChatRoom | null>(null)

  useEffect(() => {
    startWS()
  }, [])

  // Pull the onboarding review queue for the current household once connected;
  // the server pushes updates live thereafter (LW-2a).
  useEffect(() => {
    if (connected && organizationID) listProvisioningProposals(organizationID)
  }, [connected, organizationID])

  // On mount, pull cross-org pending approvals into the store so the
  // banner count reflects requests for agents this device isn't
  // currently subscribed to.
  useEffect(() => {
    let cancelled = false
    fetchPendingApprovals()
      .then((r) => {
        if (cancelled) return
        if (!r.entries) return
        const s = useAppStore.getState()
        const existingIDs = new Set(s.pendingRequests.map((p) => p.id))
        for (const e of r.entries) {
          if (!existingIDs.has(e.request_id)) {
            s.enqueueRequest(normalizePendingApprovalEntry(e))
          }
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  const { perpetual, temp } = useMemo(() => {
    const rows: Row[] = []
    for (const a of agentInstances) {
      if (a.status === 'retired') continue
      const position = positions.find((p) => p.id === a.organization_position_id)
      const host = hosts.find((h) => h.host_id === a.host_id)
      rows.push({
        agent: a,
        positionName: position?.name ?? '(unknown position)',
        hostname: host?.hostname ?? '—',
        hostOnline: host?.online ?? false,
        hostDesiredConnected: host?.desired_status !== 'disconnected',
      })
    }
    const byName = (x: Row, y: Row) =>
      x.agent.name.localeCompare(y.agent.name, undefined, { sensitivity: 'base' })
    const perpetual = rows.filter((r) => !r.agent.is_temp).sort(byName)
    const temp = rows.filter((r) => r.agent.is_temp).sort((x, y) => {
      const xa = x.agent.status === 'active'
      const ya = y.agent.status === 'active'
      if (xa !== ya) return xa ? -1 : 1
      const xt = x.agent.last_activity_at ?? ''
      const yt = y.agent.last_activity_at ?? ''
      if (xt !== yt) return xt < yt ? 1 : -1
      return byName(x, y)
    })
    return { perpetual, temp }
  }, [agentInstances, positions, hosts])

  const memberRooms = chatRooms.filter((r) => r.is_member)
  const nonMemberRooms = chatRooms.filter((r) => !r.is_member)

  function toggle(section: Section) {
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }))
  }

  return (
    <div className="home">
      <NavBar
        title={currentOrgName}
        onTitleClick={
          canSwitchOrg
            ? () =>
                setOrgPickerOpen((o) => {
                  // Freshen the per-household chips at the moment they're shown.
                  if (!o) void fetchAttention()
                  return !o
                })
            : undefined
        }
        subtitle={
          connectionStatus !== 'connected' ? (
            <ConnectionChip status={connectionStatus} />
          ) : undefined
        }
        trailing={
          <NavBarButton
            icon={<span style={{ fontSize: 18 }}>🔔</span>}
            label="Attention"
            onClick={() => pushSheet({ kind: 'attention' })}
            badge={attentionCount}
            tint={attentionCount > 0 ? undefined : 'var(--label-tertiary)'}
          />
        }
      />

      {orgPickerOpen && (
        <OrgPickerDropdown
          orgs={availableOrganizations}
          onClose={() => setOrgPickerOpen(false)}
        />
      )}

      {/* The "Upgrade to Pro" banner was removed when entitlement enforcement
          was disconnected — every household is free. Browser push is no longer
          gated on is_pro; the banner self-guards for native/permission/dismissed.
          See docs/entitlements-disconnected.md. */}
      <EnableBrowserPushBanner />

      {/* Setup suggestions no longer get a banner here: they're in the bell's
          Attention inbox, across every household (attention_suggestions.go). */}

      <div className="home__scroll">
        <SectionHeader
          title="Human Chats"
          count={chatRooms.length}
          collapsed={collapsed.chats}
          onToggle={() => toggle('chats')}
        />
        {!collapsed.chats && (
          <div className="home__section-body">
            {chatRooms.length === 0 ? (
              <div className="home__empty">
                {connected ? 'No chat rooms yet.' : 'Connecting…'}
              </div>
            ) : (
              <>
                {memberRooms.map((room) => (
                  <RoomRow
                    key={room.id}
                    room={room}
                    unread={chatUnreadCounts[room.id] ?? 0}
                    unreadThreads={unreadThreadsByRoom[room.id] ?? 0}
                    muted={chatNotifModes[room.id] === 'muted'}
                    onTap={() =>
                      pushNav({ kind: 'chat_room', roomID: room.id, roomName: room.name })
                    }
                    onLongPress={() => setMenuRoom(room)}
                  />
                ))}
                {nonMemberRooms.length > 0 && memberRooms.length > 0 && (
                  <div className="chat-list__section-header">Other Rooms</div>
                )}
                {nonMemberRooms.map((room) => (
                  <RoomRow
                    key={room.id}
                    room={room}
                    unread={0}
                    muted={false}
                    dimmed
                    onTap={() => {}}
                    onLongPress={() => setMenuRoom(room)}
                  />
                ))}
              </>
            )}
          </div>
        )}

        <SectionHeader
          title="Full-time Agents"
          count={perpetual.length}
          collapsed={collapsed.full_time}
          onToggle={() => toggle('full_time')}
        />
        {!collapsed.full_time && (
          <div className="home__section-body">
            {perpetual.length === 0 ? (
              <div className="home__empty">
                {connected ? 'No full-time agents yet.' : 'Connecting…'}
              </div>
            ) : (
              perpetual.map((row) => (
                <AgentRow
                  key={row.agent.id}
                  row={row}
                  onTap={() =>
                    pushNav({
                      kind: 'agent_detail',
                      agentInstanceID: row.agent.id,
                      positionID: row.agent.organization_position_id,
                    })
                  }
                  onLongPress={() => setActionMenuRow(row)}
                />
              ))
            )}
          </div>
        )}

        <SectionHeader
          title="Temp Agents"
          count={temp.length}
          collapsed={collapsed.temp}
          onToggle={() => toggle('temp')}
        />
        {!collapsed.temp && (
          <div className="home__section-body">
            {temp.length === 0 ? (
              <div className="home__empty">
                {connected ? 'No temp agents yet.' : 'Connecting…'}
              </div>
            ) : (
              temp.map((row) => (
                <AgentRow
                  key={row.agent.id}
                  row={row}
                  onTap={() =>
                    pushNav({
                      kind: 'agent_detail',
                      agentInstanceID: row.agent.id,
                      positionID: row.agent.organization_position_id,
                    })
                  }
                  onLongPress={() => setActionMenuRow(row)}
                />
              ))
            )}
          </div>
        )}
      </div>

      <button
        className="home__fab"
        onClick={() => pushSheet({ kind: 'create_entity' })}
        disabled={!connected}
        aria-label="Create new chat room or agent"
        title={!connected ? 'Not connected' : 'Create new…'}
      >
        {pendingTempSpawn ? '…' : '+'}
      </button>

      {actionMenuRow && (
        <AgentActionSheet
          row={actionMenuRow}
          onClose={() => setActionMenuRow(null)}
        />
      )}

      {menuRoom && (
        <RoomActionMenu
          room={menuRoom}
          onClose={() => setMenuRoom(null)}
          onNavigate={() => {
            setMenuRoom(null)
            pushNav({ kind: 'chat_room', roomID: menuRoom.id, roomName: menuRoom.name })
          }}
        />
      )}

      {tempSpawnError && (
        <div className="agents__spawn-error" role="alert">
          <span>{tempSpawnError}</span>
          <button onClick={() => setTempSpawnError(null)}>Dismiss</button>
        </div>
      )}
    </div>
  )
}

function SectionHeader({
  title,
  count,
  collapsed,
  onToggle,
}: {
  title: string
  count: number
  collapsed: boolean
  onToggle: () => void
}) {
  return (
    <button className="home__section-header" onClick={onToggle} aria-expanded={!collapsed}>
      <span className="home__section-title">{title}</span>
      {count > 0 && <span className="home__section-count">{count}</span>}
      <span
        className={`home__section-chevron${collapsed ? ' home__section-chevron--collapsed' : ''}`}
        aria-hidden
      >
        ›
      </span>
    </button>
  )
}

function ConnectionChip({ status }: { status: 'disconnected' | 'connecting' }) {
  const color = status === 'connecting' ? 'var(--warning)' : 'var(--danger)'
  return (
    <span className="agents__conn">
      <span
        className="agents__conn-dot"
        style={{ background: color }}
        data-pulse={status === 'connecting'}
      />
      {status === 'connecting' ? 'Connecting…' : 'Disconnected'}
    </span>
  )
}

function readCurrentBannerHidden(): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return true
  return shouldHideBrowserPushBanner({
    isNative: bridge.isNative,
    notificationPermission:
      typeof Notification === 'undefined' ? undefined : Notification.permission,
    dismissed: Boolean(localStorage.getItem(PUSH_BANNER_DISMISSED_KEY)),
  })
}

function EnableBrowserPushBanner() {
  const [hidden, setHidden] = useState(readCurrentBannerHidden)
  const [busy, setBusy] = useState(false)

  if (hidden) return null

  async function enable() {
    setBusy(true)
    try {
      const r = await bridge.call('apns.registerForPush')
      localStorage.setItem(PUSH_BANNER_DISMISSED_KEY, '1')
      setHidden(true)
      if (!r.authorized) {
        // Permission denied or dismissed. About → Browser Notifications
        // surfaces the re-entry point if the user changes their mind.
      }
    } catch {
      // Network / SW / public-key fetch failure. Don't mark dismissed.
    } finally {
      setBusy(false)
    }
  }

  function dismiss() {
    localStorage.setItem(PUSH_BANNER_DISMISSED_KEY, '1')
    setHidden(true)
  }

  return (
    <div className="agents__pro-banner agents__notif-banner">
      <span className="agents__pro-banner-icon">🔔</span>
      <span className="agents__pro-banner-text">
        Get push notifications for new requests in this browser
      </span>
      <button className="agents__notif-banner-enable" onClick={enable} disabled={busy}>
        {busy ? '…' : 'Enable'}
      </button>
      <button className="agents__notif-banner-dismiss" onClick={dismiss} aria-label="Dismiss">
        ×
      </button>
    </div>
  )
}
